import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { DEFAULT_ROUTE } from "../../routes/api/api_routes";

const UtilisateursView = ({ currentUserIsAdmin }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // <-- barre de recherche
  const [currentPage, setCurrentPage] = useState(1); // <-- page courante
  const USERS_PER_PAGE = 6; // <-- limite par page
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempRoles, setTempRoles] = useState({
    is_admin: false,
    is_coach: false,
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${DEFAULT_ROUTE}users/`);
      console.log("Utilisateurs récupérés :", response.data);
      setUsers(response.data.data);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setTempRoles({ is_admin: user.is_admin, is_coach: user.is_coach });
  };

  const closeModal = () => setSelectedUser(null);

  const confirmRoleChange = async () => {
    try {
      await axios.patch(
        `${DEFAULT_ROUTE}users/${selectedUser.id}/set_role/`,
        tempRoles
      );
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === selectedUser.id ? { ...u, ...tempRoles } : u
        )
      );
      closeModal();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rôle:", error);
    }
  };

  // Filtrer par recherche
  // Filtrer par recherche
  const filteredUsers = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return users;

    return users.filter((u) => {
      const fullName = `${u.firstName} ${u.lastName}`.toLowerCase().trim();
      const email = (u.email || "").toLowerCase().trim();
      return fullName.includes(search) || email.includes(search);
    });
  }, [users, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  return (
    <div className="list-card p-6">
      <motion.h2
        className="text-2xl font-bold mb-4 border-b-[2px] border-[#f8a13e]"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Liste des utilisateurs
      </motion.h2>

      {/* Barre de recherche */}
      <motion.div
        className="mb-4 flex gap-2 w-50 mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <input
          type="text"
          placeholder="Rechercher un utilisateur..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="border rounded px-3 py-2 flex-1"
        />
        {/* <button
          onClick={() => setCurrentPage(1)}
          className="px-4 py-2 bg-teal-800 text-white rounded hover:bg-teal-600"
        >
          Rechercher
        </button> */}
      </motion.div>

      {/* Tableau animé */}
      <motion.div
        className="overflow-x-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <table className="min-w-full bg-white border border-gray-200 rounded">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-2 px-4 border-b">Avatar</th>
              <th className="py-2 px-4 border-b">Nom</th>
              <th className="py-2 px-4 border-b">Email</th>
              <th className="py-2 px-4 border-b">Genre</th>
              <th className="py-2 px-4 border-b">Rôles</th>
              {currentUserIsAdmin && (
                <th className="py-2 px-4 border-b">Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
              {paginatedUsers.map((user, index) => (
                <motion.tr
                  key={user.id}
                  className="text-center hover:bg-gray-50"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <td className="py-2 px-4 border-b">
                    <img
                      src={
                        user.avatar ||
                        "https://images.unsplash.com/photo-1511367461989-f85a21fda167?auto=format&fit=facearea&w=400&h=400&q=80"
                      }
                      alt={user.firstName}
                      className="w-12 h-12 rounded-full mx-auto object-cover"
                    />
                  </td>
                  <td className="py-2 px-4 border-b">
                    {user.firstName} {user.lastName}
                  </td>
                  <td className="py-2 px-4 border-b">{user.email}</td>
                  <td className="py-2 px-4 border-b">{user.gender || "N/A"}</td>
                  <td className="py-2 px-4 border-b">
                    {user.is_admin && (
                      <span className="text-teal-800 font-semibold mr-2">
                        Admin
                      </span>
                    )}
                    {user.is_coach && (
                      <span className="text-blue-600 font-semibold">Coach</span>
                    )}
                    {!user.is_admin && !user.is_coach && (
                      <span>Utilisateur</span>
                    )}
                  </td>
                  {currentUserIsAdmin && (
                    <td className="py-2 px-4 border-b">
                      {!user.is_admin && (
                        <button
                          className="bg-teal-800 text-white px-3 py-1 rounded hover:bg-teal-600 transition"
                          onClick={() => openRoleModal(user)}
                        >
                          Changer rôle
                        </button>
                      )}
                    </td>
                  )}
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </motion.div>

      {/* Modal animé */}
      <AnimatePresence>
        {selectedUser && currentUserIsAdmin && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg shadow-lg w-80 p-6 relative"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-xl font-bold mb-4">
                Modifier les rôles de {selectedUser.firstName}
              </h3>
              {/* reste du contenu du modal */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
// };

export default UtilisateursView;
